// src/pages/admin/OnboardingAdmin.jsx
// Drop-in tab content for SystemAdmin.jsx — shows onboarding survey data with CSV export
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Card, CardHeader, CardTitle, CardContent,
  Button, Input, Badge,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
} from '../../components/ui';
import { Download, RefreshCw, Search } from 'lucide-react';

const ROLE_LABELS    = { solo: 'Solo', team: 'Small team', agency: 'Agency', startup: 'Startup' };
const EXP_LABELS     = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' };
const PLATFORM_ICONS = { meta_ads: '📘', google_ads: '🎯', tiktok_ads: '🎵', adsense: '💰', ad_manager: '📈', shopify: '🛍️' };

function chipList(items = [], icons = {}) {
  if (!items?.length) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(i => (
        <span key={i} className="inline-flex items-center gap-0.5 text-xs bg-accent px-1.5 py-0.5 rounded">
          {icons[i] && <span>{icons[i]}</span>} {i.replace(/_/g, ' ')}
        </span>
      ))}
    </div>
  );
}

export default function OnboardingAdmin() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Join onboarding_progress with profiles for username + email
      const { data } = await supabase
        .from('onboarding_progress')
        .select(`
          user_id, use_cases, primary_role, selected_platforms, experience_level,
          survey_completed_at, completed_at,
          profiles:user_id ( username, email, tier, created_at )
        `)
        .order('survey_completed_at', { ascending: false, nullsFirst: false });

      setRows(data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    const q = search.toLowerCase();
    return !q || r.profiles?.username?.toLowerCase().includes(q) || r.profiles?.email?.toLowerCase().includes(q);
  });

  // Stats
  const total      = rows.length;
  const surveyed   = rows.filter(r => r.survey_completed_at).length;
  const completed  = rows.filter(r => r.completed_at).length;
  const byRole     = rows.reduce((acc, r) => { if (r.primary_role) acc[r.primary_role] = (acc[r.primary_role] || 0) + 1; return acc; }, {});
  const byPlatform = rows.reduce((acc, r) => { (r.selected_platforms || []).forEach(p => { acc[p] = (acc[p] || 0) + 1; }); return acc; }, {});
  const topPlatform = Object.entries(byPlatform).sort((a, b) => b[1] - a[1])[0];

  // CSV export
  const handleExport = () => {
    const headers = ['User ID','Username','Email','Tier','Joined','Use Cases','Primary Role','Selected Platforms','Experience Level','Survey Completed','Onboarding Completed'];
    const csvRows = [
      headers.join(','),
      ...rows.map(r => [
        r.user_id,
        `"${r.profiles?.username || ''}"`,
        `"${r.profiles?.email || ''}"`,
        r.profiles?.tier || '',
        r.profiles?.created_at ? new Date(r.profiles.created_at).toLocaleDateString() : '',
        `"${(r.use_cases || []).join('; ')}"`,
        r.primary_role || '',
        `"${(r.selected_platforms || []).join('; ')}"`,
        r.experience_level || '',
        r.survey_completed_at ? new Date(r.survey_completed_at).toLocaleDateString() : '',
        r.completed_at ? new Date(r.completed_at).toLocaleDateString() : '',
      ].join(','))
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `revhapi-onboarding-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total users',     value: total },
          { label: 'Survey completed',value: surveyed },
          { label: 'Fully onboarded', value: completed },
          { label: 'Top platform',    value: topPlatform ? `${PLATFORM_ICONS[topPlatform[0]] || ''} ${topPlatform[0].replace(/_/g,' ')} (${topPlatform[1]})` : '—' },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className="text-xl font-bold mt-1">{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Role breakdown */}
      {Object.keys(byRole).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm font-semibold">Roles</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(byRole).sort((a,b) => b[1]-a[1]).map(([role, count]) => (
                <div key={role} className="flex items-center gap-2 bg-accent rounded-lg px-3 py-2 text-sm">
                  <span className="font-medium">{ROLE_LABELS[role] || role}</span>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Platform interest breakdown */}
      {Object.keys(byPlatform).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm font-semibold">Platform interest</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(byPlatform).sort((a,b) => b[1]-a[1]).map(([platform, count]) => (
                <div key={platform} className="flex items-center gap-2 bg-accent rounded-lg px-3 py-2 text-sm">
                  <span>{PLATFORM_ICONS[platform] || ''}</span>
                  <span className="font-medium">{platform.replace(/_/g,' ')}</span>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Survey responses</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-52" />
              </div>
              <Button variant="outline" onClick={load} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Use cases</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Platforms</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Surveyed</TableHead>
                  <TableHead>Done</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableEmpty colSpan={8} message="No onboarding data yet" />
                ) : filtered.map(r => (
                  <TableRow key={r.user_id}>
                    <TableCell>
                      <p className="font-medium text-sm">{r.profiles?.username || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{r.profiles?.email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.profiles?.tier === 'pro' ? 'success' : r.profiles?.tier === 'starter' ? 'default' : 'warning'}>
                        {r.profiles?.tier || 'trial'}
                      </Badge>
                    </TableCell>
                    <TableCell>{chipList(r.use_cases)}</TableCell>
                    <TableCell>
                      {r.primary_role
                        ? <span className="text-sm">{ROLE_LABELS[r.primary_role] || r.primary_role}</span>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell>{chipList(r.selected_platforms, PLATFORM_ICONS)}</TableCell>
                    <TableCell>
                      {r.experience_level
                        ? <span className="text-sm">{EXP_LABELS[r.experience_level] || r.experience_level}</span>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.survey_completed_at ? new Date(r.survey_completed_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      {r.completed_at
                        ? <Badge variant="success">✓</Badge>
                        : <Badge variant="secondary">Pending</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
